import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SettingsContext = createContext({ appName: 'Synapse', loginBg: '', colorFrom: '#a855f7', colorTo: '#ec4899', accentColor: '#a855f7', refresh: () => {} });

// Generate lighter/darker shades from a hex color
function hexToHSL(hex) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      default: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function setAccentVars(hex) {
  const [h, s] = hexToHSL(hex);
  const root = document.documentElement;
  root.style.setProperty('--accent', hex);
  root.style.setProperty('--accent-light', `hsl(${h}, ${s}%, 65%)`);
  root.style.setProperty('--accent-dark', `hsl(${h}, ${s}%, 45%)`);
  root.style.setProperty('--accent-muted', `hsl(${h}, ${s}%, 55%)`);
  root.style.setProperty('--accent-bg', `hsla(${h}, ${s}%, 50%, 0.15)`);
  root.style.setProperty('--accent-ring', `hsla(${h}, ${s}%, 50%, 0.5)`);
}

export const SettingsProvider = ({ children }) => {
  const [appName, setAppName] = useState('Synapse');
  const [loginBg, setLoginBg] = useState('');
  const [colorFrom, setColorFrom] = useState('#a855f7');
  const [colorTo, setColorTo] = useState('#ec4899');
  const [accentColor, setAccentColor] = useState('#a855f7');

  const refresh = useCallback(() => {
    fetch(`${BACKEND_URL}/api/settings`)
      .then(r => r.json())
      .then(d => {
        if (d.app_name) setAppName(d.app_name);
        if (d.login_bg) setLoginBg(d.login_bg);
        if (d.color_from) setColorFrom(d.color_from);
        if (d.color_to) setColorTo(d.color_to);
        if (d.accent_color) { setAccentColor(d.accent_color); setAccentVars(d.accent_color); }
      })
      .catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { document.title = appName; }, [appName]);
  useEffect(() => { setAccentVars(accentColor); }, [accentColor]);

  return (
    <SettingsContext.Provider value={{ appName, loginBg, colorFrom, colorTo, accentColor, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
